import { Check, ChevronDown, ChevronRight, Copy, Download, ExternalLink, FileOutput, Loader2, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { LinkedinPostPayload, NewsletterPayload, PresentationPayload, XPostPayload } from './output-api'
import type { OutputState } from './use-output'
import { authenticatedOutputUrl, useOutputAuthToken } from './output-url'

interface OutputBubbleProps {
  state: OutputState
  onDismiss: () => void
}

export function OutputBubble({ state, onDismiss }: OutputBubbleProps) {
  const isDone = state.status === 'done'
  const isError = state.status === 'error' || !!state.error

  return (
    <div
      className={cn(
        'rounded-lg border bg-card px-4 py-3 text-sm shadow-sm',
        isError && 'border-destructive/40',
      )}
    >
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {state.isRunning ? (
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          ) : isError ? (
            <X className="h-4 w-4 text-destructive" />
          ) : (
            <Check className="h-4 w-4 text-success" />
          )}
          <FileOutput className="h-4 w-4 text-muted-foreground" />
          <span className="font-semibold">
            {state.platform ? `/${state.platform}` : 'Output'} {state.taskType ? `(${state.taskType})` : ''}
          </span>
          {state.format && (
            <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] uppercase text-muted-foreground">
              {state.format}
            </span>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={onDismiss} className="h-11 px-2 min-[761px]:h-9" aria-label="Output schliessen">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {state.detail && (
        <p className="mb-2 text-xs text-muted-foreground">
          {state.status} — {state.detail}
          {state.model ? ` · ${state.model}` : ''}
        </p>
      )}

      {state.result && isDone && (
        <div className="space-y-3">
          <p className="break-all font-mono text-[12px] text-foreground">{state.result.vault_path}</p>

          {state.taskType === 'linkedin_post' && state.jsonPayload && (
            <LinkedinPostView payload={state.jsonPayload as LinkedinPostPayload} />
          )}
          {state.taskType === 'x_post' && state.jsonPayload && (
            <XPostView payload={state.jsonPayload as XPostPayload} />
          )}
          {state.taskType === 'newsletter' && state.jsonPayload && (
            <NewsletterView payload={state.jsonPayload as NewsletterPayload} />
          )}
          {state.taskType === 'praesentation' && state.jsonPayload && (
            <PresentationView payload={state.jsonPayload as PresentationPayload} />
          )}

          {state.result.storage_url && state.taskType !== 'praesentation' && (
            <Button asChild size="sm" variant="outline">
              <a href={state.result.storage_url} target="_blank" rel="noopener noreferrer">
                <Download className="mr-1.5 h-3.5 w-3.5" />
                Herunterladen
              </a>
            </Button>
          )}
        </div>
      )}

      {state.error &&
        (isPresentationFailure(state) ? (
          <PresentationErrorView message={state.error} />
        ) : (
          <p className="text-xs text-destructive">{state.error}</p>
        ))}
    </div>
  )
}

const PRESENTATION_ERROR_PREFIXES = [
  /^Präsentations-QA fehlgeschlagen:\s*/,
  /^Presentation QA failed:\s*/,
  /^Slide-Spec konnte nicht valide erzeugt werden[^:]*:\s*/,
  /^Slide-Spec invalid:\s*/,
]

/** Zeilen, die keine Befunde sind, sondern Persistierungs-Hinweise des Backends. */
const DIAGNOSTIC_NOTE_PATTERN = /abgelegt:\s*\[\[/

function isPresentationFailure(state: OutputState): boolean {
  if (!state.error) return false
  if (state.taskType === 'praesentation' || state.platform === 'presentation') return true
  return /presentation|slide-spec|praesentation/i.test(state.error)
}

interface ParsedPresentationError {
  issues: string[]
  /** True, wenn das Backend QA-Diagnose/Draft unter presentation/qa abgelegt hat. */
  diagnosticsSaved: boolean
}

/**
 * M3-A: Presentation-Fehler kommen als strukturierter Multi-Issue-Text
 * ("Presentation QA failed: a; b; QA-Diagnose abgelegt: [[...]]" bzw.
 * Slide-Spec-Exhaustion mit " | "-getrennten Versuchen). Aufsplitten in eine
 * lesbare Issue-Liste statt einer rohen Fehlerzeile.
 */
export function parsePresentationError(message: string): ParsedPresentationError {
  let text = message.trim()
  for (const prefix of PRESENTATION_ERROR_PREFIXES) {
    text = text.replace(prefix, '')
  }
  const parts = text
    .split(/\s*(?:;|\||\n)\s*/)
    .map((part) => part.trim())
    .filter(Boolean)
  const issues = parts.filter((part) => !DIAGNOSTIC_NOTE_PATTERN.test(part))
  return {
    issues: issues.length > 0 ? issues : [message],
    diagnosticsSaved: /_Outputs\/presentation\/qa\//.test(message),
  }
}

function PresentationErrorView({ message }: { message: string }) {
  const { issues, diagnosticsSaved } = parsePresentationError(message)
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-destructive">
        Die Präsentation konnte nicht erstellt werden
      </p>
      <ul className="list-disc space-y-1 pl-5 text-xs text-destructive">
        {issues.map((issue, index) => (
          <li key={index}>{issue}</li>
        ))}
      </ul>
      {diagnosticsSaved && (
        <p className="text-xs text-muted-foreground">
          Die QA-Diagnose und ein Draft-HTML wurden im Vault unter _Outputs/presentation/qa/
          abgelegt.
        </p>
      )}
    </div>
  )
}

function PresentationView({ payload }: { payload: PresentationPayload }) {
  const [showMarkdown, setShowMarkdown] = useState(false)
  const token = useOutputAuthToken()
  const htmlUrl = authenticatedOutputUrl(payload.html_url, token)
  const pdfUrl = authenticatedOutputUrl(payload.pdf_url, token)
  const markdown = payload.raw_markdown ?? payload.rawMarkdown
  const meta = [
    'Präsentation',
    typeof payload.slide_count === 'number' ? `${payload.slide_count} Folien` : null,
    payload.aspect,
  ].filter(Boolean).join(' · ')

  return (
    <div className="space-y-3 rounded border border-border bg-muted/20 p-3">
      <div className="text-sm font-medium text-foreground">{meta}</div>
      <div className="flex flex-wrap gap-2">
        {htmlUrl && (
          <Button asChild size="sm" variant="outline">
            <a href={htmlUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              HTML öffnen
            </a>
          </Button>
        )}
        {pdfUrl && (
          <Button asChild size="sm">
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" download>
              <Download className="mr-1.5 h-3.5 w-3.5" />
              PDF herunterladen
            </a>
          </Button>
        )}
        {markdown && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setShowMarkdown((value) => !value)}
          >
            {showMarkdown ? <ChevronDown className="mr-1.5 h-3.5 w-3.5" /> : <ChevronRight className="mr-1.5 h-3.5 w-3.5" />}
            Inhalt anzeigen
          </Button>
        )}
      </div>
      {markdown && showMarkdown && (
        <div className="rounded border border-border bg-background">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Markdown
            </span>
            <CopyButton value={markdown} label="MD kopieren" small />
          </div>
          <pre className="max-h-80 overflow-auto whitespace-pre-wrap p-3 font-mono text-[12px] leading-relaxed text-foreground">
            {markdown}
          </pre>
        </div>
      )}
    </div>
  )
}

function NewsletterView({ payload }: { payload: NewsletterPayload }) {
  const copyValue = [
    `Subject: ${payload.subject}`,
    payload.preheader ? `Preheader: ${payload.preheader}` : '',
    '',
    payload.body,
    '',
    `CTA: ${payload.cta}`,
  ].filter(Boolean).join('\n')

  return (
    <div className="space-y-3 rounded border border-border bg-muted/20 p-3">
      <SectionHeader title="Newsletter" copyValue={copyValue} />
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Subject
        </div>
        <p className="mt-1 font-medium">{payload.subject}</p>
      </div>
      {payload.preheader && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Preheader
          </div>
          <p className="mt-1 text-muted-foreground">{payload.preheader}</p>
        </div>
      )}
      <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">{payload.body}</pre>
      <div className="border-t border-border pt-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          CTA
        </div>
        <p className="mt-1">{payload.cta}</p>
      </div>
    </div>
  )
}

function LinkedinPostView({ payload }: { payload: LinkedinPostPayload }) {
  const hashtagsLine = payload.hashtags
    .map((h) => (h.startsWith('#') ? h : `#${h}`))
    .join(' ')
  const fullPost = `${payload.post}${hashtagsLine ? `\n\n${hashtagsLine}` : ''}`

  return (
    <div className="space-y-3 rounded border border-border bg-muted/20 p-3">
      <SectionHeader title="Post" copyValue={fullPost} count={`${payload.post.length}/1300`} />
      <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">{payload.post}</pre>

      {payload.hashtags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {payload.hashtags.map((h) => (
            <span
              key={h}
              className="rounded bg-primary/10 px-2 py-0.5 font-mono text-[11px] text-primary"
            >
              {h.startsWith('#') ? h : `#${h}`}
            </span>
          ))}
        </div>
      )}

      {payload.first_comment && (
        <div className="border-t border-border pt-3">
          <SectionHeader title="First Comment" copyValue={payload.first_comment} />
          <pre className="mt-1 whitespace-pre-wrap font-sans text-sm text-muted-foreground">
            {payload.first_comment}
          </pre>
        </div>
      )}
    </div>
  )
}

function XPostView({ payload }: { payload: XPostPayload }) {
  const fullThread = payload.thread.join('\n\n---\n\n')
  const tweets = payload.thread

  return (
    <div className="space-y-3 rounded border border-border bg-muted/20 p-3">
      <SectionHeader
        title={`Thread (${tweets.length} Tweets)`}
        copyValue={fullThread}
        copyLabel="Thread kopieren"
      />
      <ul className="space-y-2">
        {tweets.map((tweet, i) => (
          <li key={i} className="rounded border border-border bg-background p-2">
            <div className="mb-1 flex items-center justify-between">
              <span className="font-mono text-[11px] text-muted-foreground">
                {i + 1}/{tweets.length}
              </span>
              <span
                className={cn(
                  'font-mono text-[11px]',
                  tweet.length > 280 ? 'text-destructive' : 'text-muted-foreground',
                )}
              >
                {tweet.length}/280
              </span>
              <CopyButton value={tweet} small />
            </div>
            <pre className="whitespace-pre-wrap font-sans text-sm">{tweet}</pre>
          </li>
        ))}
      </ul>
      {payload.single && (
        <div className="border-t border-border pt-3">
          <SectionHeader title="1-Tweet-Variante" copyValue={payload.single} />
          <pre className="mt-1 whitespace-pre-wrap font-sans text-sm text-muted-foreground">
            {payload.single}
          </pre>
        </div>
      )}
    </div>
  )
}

function SectionHeader({
  title,
  copyValue,
  copyLabel,
  count,
}: {
  title: string
  copyValue: string
  copyLabel?: string
  count?: string
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </span>
        {count && <span className="font-mono text-[11px] text-muted-foreground">{count}</span>}
      </div>
      <CopyButton value={copyValue} label={copyLabel} />
    </div>
  )
}

function CopyButton({
  value,
  label,
  small = false,
}: {
  value: string
  label?: string
  small?: boolean
}) {
  const [copied, setCopied] = useState(false)

  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      toast.success('Kopiert')
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error('Kopieren fehlgeschlagen')
    }
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      className={cn('gap-1', small && 'h-6 px-1.5 text-[11px]')}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {label ?? (small ? '' : 'Kopieren')}
    </Button>
  )
}
